import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAuthRoles, selectAuthUserTypes } from "../../../features/auth/authSlice";
import { USER_TYPES } from "../../../utils/userTypes";
import classes from "./Sidebar.module.css";

const arrowRight = "/images/right-arrow.png";
const arrowDown = "/images/downicon.png";
const Sidebar = () => {
  const permittedPages = useSelector((state) => state.auth.permission) || [];
  const permittedUrls = new Set(permittedPages.map((p) => p.modulePageUrl));
  const userRoles = useSelector(selectAuthRoles);
  const userTypes = useSelector(selectAuthUserTypes);

  const isOperator =
    userTypes.includes(USER_TYPES.OPERATOR) ||
    userRoles.some((role) => role.toLowerCase().includes("operator"));

  const iconMapping = {
    Dashboard: "/images/svg/dashboard.svg",
    Plan: "/images/svg/plan.svg",
    "Explore Plans": "/images/svg/plan.svg",
    "Master Wallet": "/images/svg/wallets.svg",
    Vendors: "/images/svg/vendor.svg",
    Operators: "/images/svg/Operators.svg",
    Payments: "/images/svg/payment.svg",
    Issuance: "/images/svg/issuance.svg",
    "Buy Policy": "/images/svg/issuance.svg",
    User: "/images/svg/user.svg",
    Wallet: "/images/svg/wallets.svg",
    "Assign Plans": "/images/svg/plan.svg",
    "Your Plans": "/images/svg/plan.svg",
    "Cover": "/images/svg/plan.svg",
    Commission: "/images/svg/commission.svg",
    Policies: "/images/svg/plan.svg",
    "User Wallets": "/images/svg/wallet.svg",
    Website: "/images/svg/website.svg",
    "Booking Management": "/images/svg/booking.svg",
    "Bulk Email": "/images/svg/bulk-mail.svg",
    "All Policy": "/images/svg/issuance.svg",
    "All policy": "/images/svg/issuance.svg",
    "User Details": "/images/svg/issuance.svg",
    "Sales": "/images/svg/vendor.svg",
  };

  
   const menuItems = [
    { label: "Dashboard", link: "/dashboard" },
    ...(isOperator
      ? [
          { label: "Your Plans", link: "/operator/operator-details/operator-assign-covers" },
          { label: "Wallet", link: "/operator/operator-details/wallet" },
          { label: "Payments", link: "/operator/operator-details/payments" },
          { label: "All policy", link: "/operator/operator-details/policies-issued" },
        ]
      : []),
    {
      label: "Plan", 
      link: "/cover-api",
      subMenu: [
        { label: "Activity", link: "/cover-api/adventures" },
        { label: "Plan Benefits", link: "/cover-api/benefits" },
        { label: "Mother Plan", link: "/cover-api/mother-covers" },
        { label: "Master Plan", link: "/cover-api/master-covers" },
        { label: "Customize Plan", link: "/cover-api/customize-covers" },
        // { label: "Plan Approval", link: "/cover-api/plan-approval" },
        { label: "Reports", link: "/cover-api/plan-reports" },
      ],
    },
    {
      label: "Sales", 
      link: "/sales/leads",
    },
    {
      label: "Explore Plans",
      link: "/cover-api",
      subMenu: [  
        { label: "View Explore Plans", link: "/cover-api/customize-covers-view" },
        { label: "Create Explore Plans", link: "/cover-api/explore-plans" },
      ],
    },
    { label: "Master Wallet", link: "/asc-wallet/master-wallet" },
    {
      label: "User",
      link: "/user/users",
    },
    {
      label: "Vendors",
      link: "/vendor/vendors",
    },
    {
      label: "Operators",
      link: "/operator/operators",
    },
    {
      label: "Payments",
      link: "/asc-wallet/payments",
    },
    {
      label: isOperator ? "Buy Policy" : "Issuance",
      link: "/issuance",
      subMenu: [
        {
          label: isOperator ? "Buy Single Policy" : "Single Issuance",
          link: "/issuance/single-issuance",
        },
        {
          label: isOperator ? "Buy Multiple Policy" : "Bulk Issuance",
          link: "/issuance/bulk-issuance",
        },
        { label: "Quote Links", link: "/issuance/quote-links" },
      ],
    },  
    {
      label: "Website",
      link: "/website",
      subMenu: [
        { label: "Cover Enquiries", link: "/website/cover-enquiries" },
        { label: "Payments", link: "/website/website-payments" },
        { label: "Promotions", link: "/website/promotions" },
        { label: "FAQs", link: "/website/faqs" },
      ],
    },
    { label: "All Policy", link: "/issuance/all-policy" },
    {
      label: "Bulk Email",
      link: "/bulk-email",
      subMenu: [
        { label: "Static Email Templates", link: "/bulk-email/static-email" },
        { label: "Compose Email", link: "/bulk-email/compose-email" },
        { label: "Email Logs", link: "/bulk-email/emaillogs" },
      ],
    },
  ];

  // State to track open submenu
  const [openSubMenu, setOpenSubMenu] = useState(null);

  const toggleSubMenu = (label) => {
    setOpenSubMenu(openSubMenu === label ? null : label);
  };

  const isPermitted = (link) =>
    [...permittedUrls].some((permitted) => link.includes(permitted));

  const filteredMenuItems = menuItems
    .map((item) => {
      const mainPermitted = isPermitted(item.link);
      const allPolicyPermitted =
        item.label === "All Policy" &&
        (isPermitted("/issuance/all-policy") || isPermitted("/issuance/all-travelers"));
      if (mainPermitted || allPolicyPermitted || item.subMenu?.some((sub) => isPermitted(sub.link))) {
        return {
          ...item,
          subMenu: item.subMenu?.filter((sub) => isPermitted(sub.link)) || [],
        };
      }
      return null;
    })
    .filter(Boolean);
 

  return (
     <div className={classes.sidebar}>
      <ul className={classes.sidebarList}>
        {filteredMenuItems.map((item) => (
          <li key={item.label} className={classes.sidebarItem}>
            {item.subMenu.length ? (
              <>
                <button className={classes.sidebarButton} onClick={() => toggleSubMenu(item.label)}>
                  <img src={iconMapping[item.label]} alt={item.label} className={classes.sidebarIcon} />
                  {item.label}
                  <img src={openSubMenu === item.label ? arrowDown : arrowRight} alt="Toggle" className={classes.arrowIcon} />
                </button>
                {openSubMenu === item.label && (
                  <ul className={classes.sidebarSublist}>
                    {item.subMenu.map((sub) => (
                      <li key={sub.link} className={classes.sidebarSubitem}>
                        <NavLink to={sub.link} className={({ isActive }) => (isActive ? `${classes.sidebarLink} ${classes.active}` : classes.sidebarLink)}>
                          {sub.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <NavLink
                to={item.link}
                isActive={
                  item.label === "All Policy"
                    ? (_, { pathname }) =>
                        pathname.includes("/issuance/all-policy") ||
                        pathname.includes("/issuance/all-travelers")
                    : item.label === "All policy"
                      ? (_, { pathname }) =>
                          pathname.includes("/operator/operator-details/policies-issued")
                      : undefined
                }
                className={({ isActive }) =>
                  isActive ? `${classes.sidebarLink} ${classes.active}` : classes.sidebarLink
                }
              >
                <img src={iconMapping[item.label]} alt={item.label} className={classes.sidebarIcon} />
                {item.label}
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </div>
    
  );
};

export default Sidebar;